import { Component, PLATFORM_ID, inject, signal, input, output, ChangeDetectionStrategy, computed, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { GoogleLoginComponent } from '../google-login/google-login.component';
import { AppleLoginComponent } from '../apple-login/apple-login.component';
import { SignupStateService } from '../signup-state.service';

import { AuthService } from '../../../shared/services/auth.service';
import { UserApiService } from '../../../shared/services/user-api.service';
import { SubscriptionService } from '../../../shared/services/subscription.service';
import { AnalyticsUtils } from '../../../shared/services/analytics-utils';
import { ReturnUrlService } from '../../../shared/services/return-url.service';
import { SharedService } from '../../../shared/services/shared.service';
import { PricingService } from '../../../features/pricing/services/pricing.service';
import { loadWatchlistSymbols } from '../../../stores/watchlist-symbols/watchlist-symbols.actions';

@Component({
   selector: 'app-login',
   templateUrl: './login.component.html',
   styleUrls: ['./login.component.scss'],
   imports: [GoogleLoginComponent, AppleLoginComponent, RouterLink, ReactiveFormsModule, NgClass],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit {
   private fb = inject(FormBuilder);
   private platformId = inject(PLATFORM_ID);
   private auth = inject(AuthService);
   private userApiService = inject(UserApiService);
   private subscriptionService = inject(SubscriptionService);
   private analyticsUtils = inject(AnalyticsUtils);
   private returnUrlService = inject(ReturnUrlService);
   private router = inject(Router);
   private route = inject(ActivatedRoute);
   private store = inject(Store);
   private sharedService = inject(SharedService);
   private pricingService = inject(PricingService);
   private signupState = inject(SignupStateService);

   private isBrowser = isPlatformBrowser(this.platformId);

   isInsidePopup = input<boolean>(false);
   isRedirecting = signal(false);
   
   loginSuccess = output<void>();
   signupRequested = output<void>();

   headerText = input<string>("Welcome back to <strong class=\"tw-font-semibold\">Musaffa</strong>!");
   subheaderText = input<string>("");
   showFormDirectly = input<boolean>(true);

   isEmailFormVisible = signal(true);

   showPassword = signal(false);
   isSubmitting = signal(false);
   errorMessage = signal<string>('');
   showFormRequested = output<void>();

   loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.pattern(/[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+([a-z]{1,5})\b/)]],
      password: ['', [Validators.required]]
   });

   private queryParamsSignal = toSignal(this.route.queryParams, { initialValue: {} as any });
   authQueryParams = computed(() => {
      const params = this.queryParamsSignal();
      const filtered: any = {};
      
      // Preserve source if valid
      const source = params['source'];
      if (source === 'trade' || source === 'academy') {
         filtered['source'] = source;
      }

      // Preserve return_url (Crucial for OAuth flow)
      if (params['return_url']) {
         filtered['return_url'] = params['return_url'];
      }

      // Preserve other relevant params
      if (params['redirect']) filtered['redirect'] = params['redirect'];
      if (params['featurebase']) filtered['featurebase'] = params['featurebase'];

      return filtered;
   });

   ngOnInit(): void {
      // Set initial visibility based on input
      this.isEmailFormVisible.set(this.showFormDirectly());

      if (this.isBrowser) {
         this.returnUrlService.captureReturnUrl();
         this.analyticsUtils.LoginScreen_Opened();

         // Auto-redirect if already logged in and has return_url
         if (this.auth.isLoggedIn()) {
            const returnUrl = this.returnUrlService.getReturnUrl();
            if (returnUrl) {
               this.isRedirecting.set(true);
               const token = this.auth.getToken();
               this.returnUrlService.redirectAfterAuth('/', token);
            }
         }
      }
   }

   togglePasswordVisibility() {
      this.showPassword.update(val => !val);
   }

   async login() {
      if (!this.loginForm.valid) {
         this.loginForm.markAllAsTouched();
         return;
      }
      if (!this.isBrowser) return;

      this.isSubmitting.set(true);
      this.errorMessage.set('');
      const email = this.loginForm.value.email!;
      const password = this.loginForm.value.password!;

      try {
         const resp: any = await this.userApiService.login(email, password);
         this.analyticsUtils.LoginScreen_Login_Tapped(email);

         if (resp.status) {
            if (resp.status_code === 'email_verified') {
               const token = resp.data.token;
               await this.auth.setToken(token);
               const userData = await this.userApiService.fetchUserDetailsAndSave();
               this.auth.setLoggedIn();
               this.store.dispatch(loadWatchlistSymbols());
               await this.subscriptionService.setUserSubscriptionInfo();

               this.analyticsUtils.LoginScreen_Login_Completed(email);

               if (!userData?.sign_up_completed) {
                  if (this.isInsidePopup()) {
                     this.auth.closeAuthPopup();
                  }
                  this.router.navigate(['/authentication/activate'], { queryParams: this.authQueryParams() });
                  return;
               }

               // const subInfo = SubscriptionService.subscriptionData.getValue();
               // if (subInfo.role === 'FREEMIUM') {
               //    this.sharedService.openUpgradePopup();
               // }

               if (this.isInsidePopup()) {
                  this.loginSuccess.emit();
               } else {
                  const returnUrl = this.returnUrlService.getReturnUrl();
                  if (returnUrl) {
                     this.returnUrlService.redirectAfterAuth('/', token);
                  } else {
                     this.redirectUser();
                  }
               }
            } else {
               // Account exists but email is not verified
               this.signupState.setSignupData(email, password);
               await this.userApiService.sendOtpMail({ email });
               
               if (this.isInsidePopup()) {
                  this.signupRequested.emit(); 
               } else {
                  this.router.navigate(['/authentication/registration'], { 
                     queryParams: this.authQueryParams()
                  });
               }
            }
         } else {
            this.errorMessage.set(resp.message || 'Login failed');
         }
      } catch (error) {
         this.errorMessage.set('Something went wrong');
         this.analyticsUtils.LoginScreen_Login_Failed(email, 'Something went wrong');
      } finally {
         this.isSubmitting.set(false);
      }
   }

   handleLinkClick(event: Event, type: 'signup' | 'forgot-password' | 'legal') {
      if (this.isInsidePopup()) {
         if (type === 'signup') {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.signupRequested.emit();
         } else {
            // Close modal for 'forgot-password' or 'legal' links to allow navigation
            this.auth.closeAuthPopup();
         }
      }
   }

   private redirectUser() {
      const featurebase = this.route.snapshot.queryParams['featurebase'];
      if (featurebase === '1') {
         this.pricingService.getJwtToken().then(response => {
            const jwtToken = response?.jwt;
            if (jwtToken) {
               window.location.href = `https://musaffa.featurebase.app/api/v1/auth/access/jwt?jwt=${jwtToken}&return_to=https://musaffa.featurebase.app/en/widget?page=MainView`;
            }
         });
         return;
      }

      let redirectURL = localStorage.getItem('redirectUrl');
      if (redirectURL?.includes('?')) {
         redirectURL = "/";
      }

      if (redirectURL) {
         this.router.navigateByUrl(redirectURL);
      } else {
         this.router.navigate(['/'], { queryParams: this.authQueryParams() });
      }
   }

   public toggleEmailForm() {
      this.isEmailFormVisible.set(true);
      this.showFormRequested.emit();
   }
}
